import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';
import { useUiConfirmModalStore } from '@/store/ui';
import { SingleTableBoxScene } from '@/shared/components/ui';
import {
  useActualizarCostoEquipo,
  useActualizarMotivoExcepcion,
  useActualizarMotivoRetiro,
  useActualizarTipoIncidencia,
  useCrearCostoEquipo,
  useCrearMotivoExcepcion,
  useCrearMotivoRetiro,
  useCrearTipoIncidencia,
  useFetchAdminCostosEquipo,
  useFetchAdminMotivosExcepcion,
  useFetchAdminMotivosRetiro,
  useFetchAdminTiposIncidencia,
  useToggleCostoEquipo,
  useToggleMotivoExcepcion,
  useToggleMotivoRetiro,
  useToggleTipoIncidencia,
} from '@/shared/actions/erp/tickets-soporte-cliente/recepcion-equipos.actions';
import { TabContext, TabList, TabPanel } from '@mui/lab';

interface CatalogForm {
  nombre: string;
  descripcion: string;
  tipoIncidenciaId: number | null;
  item: string;
  valorTotal: string;
  grupo: string;
}

const emptyForm: CatalogForm = { nombre: '', descripcion: '', tipoIncidenciaId: null, item: '', valorTotal: '', grupo: '' };

const RecepcionEquiposAdminPage = () => {
  const [tabValue, setTabValue] = useState('1');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CatalogForm>(emptyForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<string>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore((s) => s.setConfirmDialogIsOpen);

  // Tipo Incidencia hooks
  const { data: tiposRes, isLoading: tiposLoading } = useFetchAdminTiposIncidencia();
  const tiposIncidencia = (tiposRes as any)?.data ?? [];
  const crearTipo = useCrearTipoIncidencia();
  const actualizarTipo = useActualizarTipoIncidencia();
  const toggleTipo = useToggleTipoIncidencia();

  // Motivo Retiro hooks
  const { data: motivosRes, isLoading: motivosLoading } = useFetchAdminMotivosRetiro();
  const motivosRetiro = useMemo(() => (motivosRes as any)?.data ?? [], [motivosRes]);
  const crearMotivo = useCrearMotivoRetiro();
  const actualizarMotivo = useActualizarMotivoRetiro();
  const toggleMotivo = useToggleMotivoRetiro();

  // Costos Equipo hooks
  const { data: costosRes, isLoading: costosLoading } = useFetchAdminCostosEquipo();
  const costosEquipo = useMemo(() => (costosRes as any)?.data ?? [], [costosRes]);
  const crearCosto = useCrearCostoEquipo();
  const actualizarCosto = useActualizarCostoEquipo();
  const toggleCosto = useToggleCostoEquipo();

  // Motivos Excepcion hooks
  const { data: excepcionesRes, isLoading: excepcionesLoading } = useFetchAdminMotivosExcepcion();
  const motivosExcepcion = useMemo(() => (excepcionesRes as any)?.data ?? [], [excepcionesRes]);
  const crearExcepcion = useCrearMotivoExcepcion();
  const actualizarExcepcion = useActualizarMotivoExcepcion();
  const toggleExcepcion = useToggleMotivoExcepcion();

  const tiposColumns = useMemo(() => [
    { key: 'id', label: 'ID' },
    { key: 'nombre', label: 'Nombre' },
    { key: 'descripcion', label: 'Descripción' },
  ], []);

  const motivosColumns = useMemo(() => [
    { key: 'id', label: 'ID' },
    { key: 'tipoIncidenciaNombre', label: 'Tipo Incidencia' },
    { key: 'nombre', label: 'Nombre' },
    { key: 'descripcion', label: 'Descripción' },
  ], []);

  const excepcionesColumns = useMemo(() => [
    { key: 'id', label: 'ID' },
    { key: 'nombre', label: 'Nombre' },
    { key: 'descripcion', label: 'Descripción' },
  ], []);

  const costosColumns = useMemo(() => [
    { key: '#', label: 'ID' },
    { key: 'item', label: 'Item' },
    { key: 'grupo', label: 'Grupo' },
    { key: 'valor_total', label: 'Valor Total', format: (v: number) => `$${Number(v).toFixed(2)}` },
  ], []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (item: any) => {
    setEditingId(item.id);
    if (tabValue === '3') {
      setForm({
        nombre: '',
        descripcion: '',
        tipoIncidenciaId: null,
        item: item.item ?? '',
        valorTotal: String(Number(item.valor_total ?? 0)),
        grupo: item.grupo ?? '',
      });
    } else if (tabValue === '4') {
      setForm({
        nombre: item.nombre,
        descripcion: item.descripcion || '',
        tipoIncidenciaId: null,
        item: '',
        valorTotal: '',
        grupo: '',
      });
    } else {
      setForm({
        nombre: item.nombre,
        descripcion: item.descripcion || '',
        tipoIncidenciaId: item.tipo_incidencia_id ?? null,
        item: '',
        valorTotal: '',
        grupo: '',
      });
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = () => {
    if (tabValue === '3') {
      if (!form.item.trim() || !form.valorTotal) return;
      const data: any = { item: form.item, valor_total: Number(form.valorTotal) };
      if (form.grupo) data.grupo = form.grupo;
      if (editingId) {
        actualizarCosto.mutate({ id: editingId, data });
      } else {
        crearCosto.mutate(data);
      }
    } else if (tabValue === '4') {
      if (!form.nombre.trim()) return;
      if (editingId) {
        actualizarExcepcion.mutate({ id: editingId, data: { nombre: form.nombre, descripcion: form.descripcion } });
      } else {
        crearExcepcion.mutate({ nombre: form.nombre, descripcion: form.descripcion });
      }
    } else if (tabValue === '1') {
      if (!form.nombre.trim()) return;
      if (editingId) {
        actualizarTipo.mutate({ id: editingId, data: { nombre: form.nombre, descripcion: form.descripcion } });
      } else {
        crearTipo.mutate({ nombre: form.nombre, descripcion: form.descripcion });
      }
    } else {
      if (!form.nombre.trim() || !form.tipoIncidenciaId) return;
      if (editingId) {
        actualizarMotivo.mutate({
          id: editingId,
          data: { tipo_incidencia_id: form.tipoIncidenciaId, nombre: form.nombre, descripcion: form.descripcion },
        });
      } else {
        crearMotivo.mutate({
          tipo_incidencia_id: form.tipoIncidenciaId,
          nombre: form.nombre,
          descripcion: form.descripcion,
        });
      }
    }
    handleClose();
  };

  const handleToggle = (id: number, type: 'tipo' | 'motivo' | 'costo' | 'excepcion') => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cambiar Estado',
      subtitle: '¿Está seguro que desea cambiar el estado de este registro?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        if (type === 'tipo') toggleTipo.mutate(id);
        else if (type === 'motivo') toggleMotivo.mutate(id);
        else if (type === 'costo') toggleCosto.mutate(id);
        else toggleExcepcion.mutate(id);
      },
    });
  };

  const handleSort = (key: string) => {
    setSortDir((d) => (sortKey === key ? (d === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const filterData = (data: any[]) => {
    if (!searchTerm.trim()) return data;
    const term = searchTerm.toLowerCase();
    return data.filter((row) =>
      Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(term))
    );
  };

  const sortData = (data: any[]) => {
    return [...data].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      if (sortKey === 'tipoIncidenciaNombre') {
        aVal = tiposIncidencia.find((t: any) => t.id === a.tipo_incidencia_id)?.nombre || '';
        bVal = tiposIncidencia.find((t: any) => t.id === b.tipo_incidencia_id)?.nombre || '';
      }
      if (sortKey === 'valor_total') {
        aVal = Number(aVal ?? 0);
        bVal = Number(bVal ?? 0);
      }
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal), 'es', { numeric: true })
        : String(bVal).localeCompare(String(aVal), 'es', { numeric: true });
    });
  };

  const renderTable = (
    data: any[],
    columns: { key: string; label: string; format?: (v: any) => string }[],
    toggleType: 'tipo' | 'motivo' | 'costo' | 'excepcion',
  ) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortKey === col.key}
                  direction={sortKey === col.key ? sortDir : 'asc'}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell sx={{ fontWeight: 700 }}>Activo</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row: any, idx: number) => (
            <TableRow key={row.id}>
              {columns.map((col, ci) => {
                if (col.key === '#') {
                  return <TableCell key={col.key}>{ci === 0 ? idx + 1 : '-'}</TableCell>;
                }
                let raw = row[col.key];
                let val: string;
                if (col.key === 'tipoIncidenciaNombre') {
                  val = tiposIncidencia.find((t: any) => t.id === row.tipo_incidencia_id)?.nombre || '-';
                } else if (col.format && raw != null) {
                  val = col.format(raw);
                } else {
                  val = raw != null ? String(raw) : '-';
                }
                return <TableCell key={col.key}>{val}</TableCell>;
              })}
              <TableCell>
                <Switch
                  checked={row.activo}
                  onChange={() => handleToggle(row.id, toggleType)}
                />
              </TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + 2} align="center">
                <Typography variant="body2" color="text.secondary">
                  No hay registros
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <SingleTableBoxScene title="Catálogos Retiro de Equipos" urlRedirect="" enableRedirect={false}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
          sx={{ minWidth: 280 }}
        />
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" onClick={handleOpenCreate}>
          Nuevo {tabValue === '1' ? 'Tipo Incidencia' : tabValue === '3' ? 'Costo Equipo' : tabValue === '4' ? 'Motivo Excepción' : 'Motivo Retiro'}
        </Button>
      </Box>

      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <TabList onChange={(_, v) => { setTabValue(v); setSearchTerm(''); }}>
            <Tab label="Tipos Incidencia" value="1" />
            <Tab label="Motivos Retiro" value="2" />
            <Tab label="Costos Equipo" value="3" />
            <Tab label="Motivos Excepción" value="4" />
          </TabList>
        </Box>

        <TabPanel value="1" sx={{ px: 0 }}>
          {tiposLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : renderTable(sortData(filterData(tiposIncidencia)), tiposColumns, 'tipo')}
        </TabPanel>

        <TabPanel value="2" sx={{ px: 0 }}>
          {motivosLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : renderTable(sortData(filterData(motivosRetiro)), motivosColumns, 'motivo')}
        </TabPanel>

        <TabPanel value="3" sx={{ px: 0 }}>
          {costosLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : renderTable(sortData(filterData(costosEquipo)), costosColumns, 'costo')}
        </TabPanel>

        <TabPanel value="4" sx={{ px: 0 }}>
          {excepcionesLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : renderTable(sortData(filterData(motivosExcepcion)), excepcionesColumns, 'excepcion')}
        </TabPanel>
      </TabContext>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Editar' : 'Crear'} {tabValue === '1' ? 'Tipo Incidencia' : tabValue === '3' ? 'Costo Equipo' : tabValue === '4' ? 'Motivo Excepción' : 'Motivo Retiro'}
        </DialogTitle>
        <DialogContent>
          {tabValue === '3' ? (
            <>
              <TextField
                  autoFocus
                  margin="dense"
                  label="Item"
                  fullWidth
                  value={form.item}
                  onChange={(e) => setForm((f) => ({ ...f, item: e.target.value }))}
                />
                <TextField
                  margin="dense"
                  label="Valor Total (con IVA)"
                  fullWidth
                  type="number"
                  value={form.valorTotal}
                  onChange={(e) => setForm((f) => ({ ...f, valorTotal: e.target.value }))}
                />
                <TextField
                  select
                  margin="dense"
                  label="Grupo"
                  fullWidth
                  value={form.grupo}
                  onChange={(e) => setForm((f) => ({ ...f, grupo: e.target.value }))}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value=""><em>Seleccione grupo</em></MenuItem>
                  <MenuItem value="FIBRA">FIBRA</MenuItem>
                  <MenuItem value="RADIO">RADIO</MenuItem>
                  <MenuItem value="VARIOS">VARIOS</MenuItem>
                </TextField>
              </>
            ) : (
              <>
                {tabValue === '2' && (
                  <Autocomplete
                    options={tiposIncidencia.filter((t: any) => t.activo)}
                    getOptionLabel={(opt: any) => opt.nombre}
                    value={tiposIncidencia.find((t: any) => t.id === form.tipoIncidenciaId) || null}
                    onChange={(_, v) => setForm((f) => ({ ...f, tipoIncidenciaId: v?.id ?? null }))}
                    renderInput={(params) => (
                      <TextField {...params} margin="dense" label="Tipo Incidencia" fullWidth required />
                    )}
                  />
                )}
                <TextField
                  autoFocus={tabValue === '1' || tabValue === '4'}
                  margin="dense"
                  label="Nombre"
                  fullWidth
                  value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                />
                <TextField
                  margin="dense"
                  label="Descripción"
                  fullWidth
                  multiline
                  rows={3}
                  value={form.descripcion}
                  onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                />
              </>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={tabValue === '3' ? !form.item.trim() || !form.valorTotal : !form.nombre.trim()}>
            {editingId ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </SingleTableBoxScene>
  );
};

export default RecepcionEquiposAdminPage;
